import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Field, inputCls, btn } from "../components/kit";
import RecordView from "../components/RecordView";
import AssignDialog from "../components/AssignDialog";
import { PersonDocuments } from "../components/Documents";
import { StartReview, PastReview } from "../components/ReviewBits";
import { TeamMemberDialog } from "../components/TeamMember";
import CertUpload from "../components/CertUpload";
import { GoalsPanel, HistoryPanel } from "../components/Growth";

function HireDate({ u, onDone }) {
  const [v, setV] = useState(u.start_date || "");
  const save = async () => { try { await api.put(`/people/${u.id}/start-date`, { start_date: v }); toast.success("Hire date updated."); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <label className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 text-sm font-semibold" data-testid="hire-date-editor">Hire date
      <input type="date" className="rounded-md border px-2 py-1 font-normal" value={v} onChange={(e) => setV(e.target.value)} data-testid="hire-date-input" />
      {v && v !== (u.start_date || "") && <button className="text-[color:var(--dcbc-red)]" onClick={save} data-testid="hire-date-save">Save</button>}
    </label>
  );
}

function SignoffDialog({ uid, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ task: "", date: new Date().toISOString().slice(0, 10), outcome: "competent", restrictions: "", next_review: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/signoffs", { ...f, user_id: uid, next_review: f.next_review || null }); toast.success("Practical sign-off recorded."); setOpen(false); onDone(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className={btn.ghost} data-testid="signoff-open-button"><ClipboardCheck className="h-4 w-4" /> Record practical sign-off</button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Practical sign-off</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="signoff-form">
          <Field label="Task or equipment" htmlFor="st"><input id="st" required className={inputCls} value={f.task} onChange={set("task")} data-testid="signoff-task-input" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Assessment date" htmlFor="sd"><input id="sd" type="date" required className={inputCls} value={f.date} onChange={set("date")} data-testid="signoff-date-input" /></Field>
            <Field label="Next review" htmlFor="sn"><input id="sn" type="date" className={inputCls} value={f.next_review} onChange={set("next_review")} data-testid="signoff-next-input" /></Field>
          </div>
          <Field label="Outcome" htmlFor="so"><select id="so" className={inputCls} value={f.outcome} onChange={set("outcome")} data-testid="signoff-outcome-select">
            <option value="competent">Competent</option><option value="competent_with_restrictions">Competent with restrictions</option><option value="not_yet_competent">Not yet competent</option></select></Field>
          <Field label="Restrictions" htmlFor="sr"><textarea id="sr" rows={2} className={inputCls} value={f.restrictions} onChange={set("restrictions")} data-testid="signoff-restrictions-input" /></Field>
          <p className="text-xs text-muted-foreground">You are recorded as the assessor.</p>
          <button className={`${btn.primary} w-full`} data-testid="signoff-submit-button">Save sign-off</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Person() {
  const { uid } = useParams();
  const [histKey, setHistKey] = useState(0);
  const { user } = useAuth();
  return (
    <div>
      <Link to="/team" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" /> My Team</Link>
      <RecordView uid={uid} canExport extra={({ data, reload }) => uid !== user.id && (
        <div className="flex flex-wrap gap-2" data-testid="person-actions">
          <AssignDialog fixedUser={uid} onDone={reload} />
          <SignoffDialog uid={uid} onDone={reload} />
          <StartReview uid={uid} />
          <PastReview uid={uid} onDone={() => setHistKey((k) => k + 1)} />
          {data?.user && <CertUpload forUser={data.user} onDone={reload} />}
          {data?.user && <HireDate key={data.user.start_date} u={data.user} onDone={reload} />}
          {data?.user && (user.is_superadmin || data.user.manager_id === user.id) && <TeamMemberDialog person={data.user} onSaved={reload} />}
          <Link to="/reviews" className={btn.ghost}>Review certificates</Link>
        </div>
      )} />
      <PersonDocuments key={histKey} uid={uid} />
      {uid !== user.id && <div className="mt-6 space-y-6"><GoalsPanel uid={uid} manage /><HistoryPanel key={histKey} uid={uid} /></div>}
    </div>
  );
}
